<script setup>
/**
 * Reusable input widget that lets users fine‑tune numeric values via typing or
 * hold‑to‑spin buttons. It guarantees the value always stays within the
 * `[min,max]` range and aligned to the specified `step` precision.
 *
 * • Native `<input type="number">` controls offer poor UX on many browsers
 *   (tiny arrow hit‑targets, inconsistent step behaviour). This component
 *   provides larger, accessible spin buttons and debounced hold‑to‑spin logic.
 * • Central place to enforce clamping, rounding, and validation so every
 *   numeric field in the app behaves consistently.
 */
import {ref, defineProps, defineEmits, watch, onBeforeUnmount, computed} from 'vue'
import {parseLocalizedDecimal, formatLocalizedDecimal, getActiveLocale} from '@/utils/format'

const props = defineProps({
  modelValue: {type: [Number, String], required: true},
  min: {type: Number, default: -Infinity},
  max: {type: Number, default: Infinity},
  step: {type: Number, default: 1},
  decimals: {type: Number, default: undefined},
  id: {type: String, default: ''},
  name: {type: String, default: ''},
  /** cross-bound guard */
  role: {type: String, default: 'value', validator: v => ['min', 'max', 'value'].includes(v)},
  peer: {type: [Number, String], default: undefined}, // the other bound
  locale: {type: String, default: ''},
})

const emit = defineEmits(['update:modelValue'])

const internal = ref(String(props.modelValue))
const locale = computed(() => props.locale || getActiveLocale())

watch(() => props.modelValue, v => {
  internal.value = String(v)
})

/** Effective bounds considering the peer value */
const effBounds = computed(() => {
  let low = props.min
  let high = props.max
  const peerNum = Number(props.peer)
  const hasPeer = Number.isFinite(peerNum)

  if (props.role === 'max' && hasPeer) low = Math.max(low, peerNum) // max can't go below min
  if (props.role === 'min' && hasPeer) high = Math.min(high, peerNum) // min can't exceed max

  if (low > high) low = high // collapse inverted range to a point
  return {low, high}
})

function clamp(n) {
  const {low, high} = effBounds.value
  return Math.min(high, Math.max(low, n))
}

/** Round using explicit `decimals` (PRICE→2, NUMBER→0). */
function roundToStep(n) {
  // Only decimal precision, no grid snap
  const inferred = (props.step.toString().split('.')[1] || '').length
  const dec = Number.isInteger(props.decimals) ? props.decimals : inferred
  return Number(n.toFixed(dec))
}

function onBlur() {
  const parsed = parseLocalizedDecimal(internal.value, {locale: locale.value})
  if (!Number.isFinite(parsed)) {
    // revert to current prop formatting without emitting
    internal.value = String(props.modelValue)
    return
  }

  const clamped = clamp(parsed)
  const rounded = roundToStep(clamped)

  // Compare against current numeric value; if unchanged, don't emit
  const currentParsed = parseLocalizedDecimal(String(props.modelValue), {locale: locale.value})
  const unchanged = Number.isFinite(currentParsed) && currentParsed === rounded

  // Always normalize the displayed text
  internal.value = formatLocalizedDecimal(rounded, {
    locale: locale.value,
    decimals: props.decimals ?? 0
  })

  if (!unchanged) {
    emit('update:modelValue', rounded)
  }
}

/** Re-clamp if bounds change (peer/min/max updates) */
watch(
  () => [effBounds.value.low, effBounds.value.high, props.step, props.decimals],
  () => {
    const parsed = parseLocalizedDecimal(internal.value, {locale: locale.value})
    if (!Number.isFinite(parsed)) return
    const clamped = clamp(parsed)
    const rounded = roundToStep(clamped)
    if (rounded !== parsed) {
      internal.value = formatLocalizedDecimal(rounded, {locale: locale.value, decimals: props.decimals ?? 0})
      emit('update:modelValue', rounded)
    }
  }
)


// hold-to-spin
let timer = null

function startSpin(delta) {
  if (timer) clearInterval(timer)
  spin(delta)
  timer = setInterval(() => spin(delta), 200)
}

function stopSpin() {
  clearInterval(timer)
  timer = null
}

/** Boundary-safe spin on the effective grid */
function spin(delta) {
  const parsed = parseLocalizedDecimal(String(internal.value), {locale: locale.value})
  const current = Number.isFinite(parsed) ? parsed : effBounds.value.low
  const nextRaw = delta > 0 ? current + props.step : delta < 0 ? current - props.step : current
  const next = clamp(roundToStep(nextRaw))
  internal.value = formatLocalizedDecimal(next, {locale: locale.value, decimals: props.decimals ?? 0})
  emit('update:modelValue', next)
}

onBeforeUnmount(() => {
  clearInterval(timer)
})
</script>


<template>
  <div class="number-spinner">
    <input
      :id="id"
      :name="name"
      type="text"
      inputmode="decimal"
      :step="step"
      :min="effBounds.low"
      :max="effBounds.high"
      v-model="internal"
      @blur="onBlur"/>
    <div class="number-spinner-actions">
      <button
        type="button"
        aria-hidden="true"
        class="spin-up"
        @mousedown.prevent="startSpin(step)"
        @mouseup="stopSpin"
        @mouseleave="stopSpin"
        @keydown.enter.prevent="spin(step)"
        @keyup.enter.prevent="stopSpin">
        <span class="icon-angle-down-before"></span>
      </button>
      <button
        type="button"
        aria-hidden="true"
        class="spin-down"
        @mousedown.prevent="startSpin(-step)"
        @mouseup="stopSpin"
        @mouseleave="stopSpin"
        @keydown.enter.prevent="spin(-step)"
        @keyup.enter.prevent="stopSpin">
        <span class="icon-angle-down-before"></span>
      </button>
    </div>
  </div>
</template>
